import { AsyncSection } from '../ui/AsyncPanel'
import { Hairline, Section } from '../ui/dashboard-primitives'
import { AiOffAnalysis } from './AiOffAnalysis'
import { MilestoneBar } from './MilestoneBar'
import { useAnalysisPanel } from './panelStates'
import { SignalsPanel } from './SignalsPanel'
import { StatusPriorities } from './StatusPriorities'
import { StrategyPanel } from './StrategyPanel'

/**
 * Sections 06 to 08, which share one analysis.
 *
 * None generated yet hides all three, as it always has, and so does a request
 * still out. A request that failed says so once, for all three, under the first
 * one's number: it is one request, so it is one failure. Through `AsyncSection`
 * (Spec 2 D5, #214), which the Dashboard used to do by hand.
 *
 * **Without AI processing** (`aiOff`, #399, CS-D14) the three sections give way to one note, and a
 * stored analysis is not shown or fetched: "Do these three things" must never be advice from months
 * ago. The Dashboard reads `aiOff` from `GET /api/me`.
 */
export function AnalysisSections({ aiOff = false }: { aiOff?: boolean }) {
  const analysis = useAnalysisPanel(!aiOff)
  if (aiOff) return <AiOffAnalysis />
  return (
    <AsyncSection
      head={{ index: '06', eyebrow: 'Analysis' }}
      sources={analysis.sources}
      hasContent={analysis.hasContent}
      subject="Your training analysis"
    >
      {() => {
        const priorities = analysis.data?.status_priorities
        const signals = analysis.data?.weak_spots ?? []
        const strategyWeeks = analysis.data?.summary_strategy?.next_4_weeks ?? []
        return (
          <>
            {/* 06 · Do these three things */}
            {priorities && priorities.priorities.length > 0 && (
              <Section index="06" eyebrow="Prescription" title="Do these three things">
                <StatusPriorities priorities={priorities.priorities} />
                <Hairline className="my-9" />
                <MilestoneBar callout={priorities.next_milestone_callout} />
              </Section>
            )}

            {/* 07 · Signals */}
            {signals.length > 0 && (
              <Section
                index="07"
                eyebrow="Diagnosis"
                title="Signals"
                subtitle="What the data says about the training block, grouped by what each one asks of you."
              >
                <SignalsPanel signals={signals} />
              </Section>
            )}

            {/* 08 · The next four weeks */}
            {strategyWeeks.length > 0 && (
              <Section index="08" eyebrow="Strategy" title="The next four weeks">
                <StrategyPanel weeks={strategyWeeks} />
              </Section>
            )}
          </>
        )
      }}
    </AsyncSection>
  )
}
